import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ISurveyAnalyticsFilter } from './analyticsModel';

/**
 * Spec 0098 — «Обзор» keeps ALL of its state in the URL: the filter (same
 * param names as the 0038 analytics, so a link can be retargeted between the
 * two screens), the tab and the open student drawer.
 *
 * Drilling (course → group → student) is a `push`, so Back undoes it; editing
 * a filter is a `replace`, so Back does not walk through every keystroke.
 */

export const INSIGHT_VIEWS = ['overview', 'comments', 'digest'] as const;
export type InsightView = typeof INSIGHT_VIEWS[number];

const LIST_PARAMS: Record<string, keyof ISurveyAnalyticsFilter> = {
  course: 'courseIds',
  group: 'groupIds',
  teacher: 'teacherIds',
  survey: 'surveyIds',
  metric: 'metrics',
};

const SCALAR_PARAMS: Record<string, keyof ISurveyAnalyticsFilter> = {
  from: 'dateFrom',
  to: 'dateTo',
};

export interface IInsightsDeepLink {
  filter: ISurveyAnalyticsFilter;
  view: InsightView;
  /** The student whose answers are open in the drawer. */
  studentId: string | null;
  /** Exactly one group picked → the group level; otherwise the course level. */
  groupId: string | null;
  /** Exactly one course picked — the scope a course digest needs. */
  courseId: string | null;
  setFilter: (next: ISurveyAnalyticsFilter) => void;
  setView: (next: InsightView) => void;
  openGroup: (groupId: string) => void;
  openStudent: (studentId: string | null) => void;
  clearOne: (field: keyof ISurveyAnalyticsFilter) => void;
  clearAll: () => void;
}

const single = (values?: string[]): string | null =>
  values && values.length === 1 ? values[0] : null;

export const useInsightsDeepLink = (): IInsightsDeepLink => {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const filter = useMemo<ISurveyAnalyticsFilter>(() => {
    const next: ISurveyAnalyticsFilter = {};
    Object.entries(LIST_PARAMS).forEach(([param, field]) => {
      const raw = params.get(param);
      if (raw) (next as Record<string, unknown>)[field] = raw.split(',');
    });
    Object.entries(SCALAR_PARAMS).forEach(([param, field]) => {
      const raw = params.get(param);
      if (raw) (next as Record<string, unknown>)[field] = raw;
    });
    return next;
  }, [params]);

  const rawView = params.get('view');
  const view: InsightView = (INSIGHT_VIEWS as readonly string[]).includes(
    rawView || '',
  )
    ? (rawView as InsightView)
    : 'overview';
  const studentId = params.get('student');

  const write = useCallback(
    (
      nextFilter: ISurveyAnalyticsFilter,
      nextView: InsightView,
      nextStudent: string | null,
      { replace = false }: { replace?: boolean } = {},
    ) => {
      const next = new URLSearchParams();
      Object.entries(LIST_PARAMS).forEach(([param, field]) => {
        const value = nextFilter[field] as string[] | undefined;
        if (value && value.length) next.set(param, value.join(','));
      });
      Object.entries(SCALAR_PARAMS).forEach(([param, field]) => {
        const value = nextFilter[field] as string | undefined;
        if (value) next.set(param, value);
      });
      if (nextView !== 'overview') next.set('view', nextView);
      if (nextStudent) next.set('student', nextStudent);
      const query = next.toString();
      const target = query ? `${pathname}?${query}` : pathname;
      if (replace) history.replace(target);
      else history.push(target);
    },
    [history, pathname],
  );

  return {
    filter,
    view,
    studentId,
    groupId: single(filter.groupIds),
    courseId: single(filter.courseIds),
    setFilter: useCallback(
      (next) => write(next, view, null, { replace: true }),
      [write, view],
    ),
    setView: useCallback(
      (next) => write(filter, next, null, { replace: true }),
      [write, filter],
    ),
    openGroup: useCallback(
      (groupId) => write({ ...filter, groupIds: [groupId] }, view, null),
      [write, filter, view],
    ),
    openStudent: useCallback(
      (next) => write(filter, view, next, { replace: !next }),
      [write, filter, view],
    ),
    clearOne: useCallback(
      (field) => {
        const next = { ...filter };
        delete next[field];
        write(next, view, null, { replace: true });
      },
      [write, filter, view],
    ),
    clearAll: useCallback(() => write({}, view, null, { replace: true }), [
      write,
      view,
    ]),
  };
};
